/**
 * Polar Analytics — helper compartido de custom events de vista.
 * Pieza central de la integracion: cachea la base async una sola vez y envia
 * los eventos via sendPixelEvent. Lo usan ST-3..ST-7 (grid y carruseles).
 *
 * window.alohasPolar.track(eventName, data)
 * window.alohasPolar.createDedupeSet()
 */
(() => {
  "use strict";

  const config = window.alohasPolarConfig ?? {};
  const { pixelEndpoint, shopifyShopURL, customer = null } = config;

  const getSdk = () => window.polarPixel ?? null;

  // El <script> auto-inyectado NO hereda el Customer Privacy de Shopify (a diferencia
  // del Web Pixel clasico): el enforcement del consentimiento es responsabilidad nuestra.
  const analyticsAllowed = () => {
    try {
      return Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed());
    } catch {
      return false;
    }
  };

  const readCustomerPrivacy = () => {
    const cp = window.Shopify?.customerPrivacy;
    if (!cp) return undefined;
    try {
      return {
        analyticsProcessingAllowed: cp.analyticsProcessingAllowed(),
        marketingAllowed: cp.marketingAllowed(),
        preferencesProcessingAllowed: cp.preferencesProcessingAllowed(),
        saleOfDataAllowed: cp.saleOfDataAllowed(),
      };
    } catch {
      return undefined;
    }
  };

  // generatePayloadBase lee localStorage y es async: se genera UNA sola vez y se
  // reutiliza para todos los eventos (gotcha de performance del SDK).
  let basePromise = null;
  const getBasePromise = () => {
    if (basePromise) return basePromise;
    const sdk = getSdk();
    if (typeof sdk?.generatePayloadBase !== "function") return null;
    basePromise = sdk.generatePayloadBase({
      // theme server-rendered: evita el placeholder literal "{{email}}" del modo headless
      isHeadless: false,
      customerPrivacy: readCustomerPrivacy(),
      customer: customer ?? undefined,
    });
    return basePromise;
  };

  // La Customer Privacy API de Shopify se inyecta async y NO existe en DOMContentLoaded, justo cuando
  // el grid nativo emite. Sin esto el evento se perdia pese a haber consentimiento (BR no lo sufre:
  // emite tras su fetch de red, ya con la API lista). Encolamos a nivel de track() el evento YA
  // construido y lo reintentamos cuando la API resuelve, asi no se re-ejecuta trackPolarItemList ni
  // se vuelve a tocar su dedupe.
  const pendingEvents = [];
  let consentApiRequested = false;

  const flushPending = () => {
    if (!window.Shopify?.customerPrivacy || !pendingEvents.length) return;
    pendingEvents.splice(0).forEach(({ eventName, data }) => track(eventName, data));
  };

  const ensureConsentApi = () => {
    if (consentApiRequested) return;
    consentApiRequested = true;
    // loadFeatures resuelve tambien el caso "ya consintio en sesion previa" (no dispara
    // visitorConsentCollected en esta carga), en el que solo el callback avisa de que la API esta lista.
    if (typeof window.Shopify?.loadFeatures === "function") {
      window.Shopify.loadFeatures(
        [{ name: "consent-tracking-api", version: "0.1" }],
        () => flushPending()
      );
    }
  };

  const track = async (eventName, data) => {
    const sdk = getSdk();
    if (!sdk || !pixelEndpoint) return;
    if (window.Shopify?.designMode) return; // no emitir eventos falsos desde el editor del theme

    if (!window.Shopify?.customerPrivacy) {
      pendingEvents.push({ eventName, data });
      ensureConsentApi();
      return;
    }
    if (!analyticsAllowed()) return;

    const bp = getBasePromise();
    if (!bp) return;

    try {
      const base = await bp;
      const evt = sdk.eventFactory.custom({
        shopifyEventName: eventName,
        shopifyEventId: crypto.randomUUID(),
        shopifyShopURL,
        shopifyEventData: data,
      });
      // useBeacon:false (default) -> fetch keepalive + retry exponencial 3x (1s/2s/4s)
      await sdk.sendPixelEvent(
        pixelEndpoint,
        base,
        { data: evt },
        { onError: (err) => console.warn("[alohasPolar] envio fallido:", eventName, err) }
      );
    } catch (err) {
      console.warn("[alohasPolar] error en track:", eventName, err);
    }
  };

  // --- Coexistencia con Bloomreach ------------------------------------------
  // BR reescribe el grid e inyecta carruseles en PDP, y emite los MISMOS custom events
  // (item_list_view / carrousel_list_view) que el theme nativo. Para no contar doble: la
  // fuente que muestra productos "reclama" la superficie; si BR no va a mostrar nada
  // (vacio o error) la "libera" y ahi el nativo emite. Asi siempre sale UN solo evento.
  const claimedSurfaces = new Set();
  const releasedSurfaces = new Set();
  const releaseCallbacks = new Map();

  const claimSurface = (surface) => {
    if (surface) claimedSurfaces.add(String(surface));
  };

  // BR la llama cuando NO mostrara productos: el nativo deja de esperar y emite su evento.
  const releaseSurface = (surface) => {
    const key = String(surface);
    if (claimedSurfaces.has(key)) return; // otra fuente ya poblo la superficie -> no liberar
    releasedSurfaces.add(key);
    (releaseCallbacks.get(key) ?? []).splice(0).forEach((cb) => cb());
  };

  // El nativo registra aqui su arranque cuando BR esta presente: corre solo si BR libera la
  // superficie (o si ya la habia liberado antes de registrarse). Si BR la reclama, nunca corre.
  const onSurfaceReleased = (surface, cb) => {
    const key = String(surface);
    if (claimedSurfaces.has(key)) return;
    if (releasedSurfaces.has(key)) return void cb();
    if (!releaseCallbacks.has(key)) releaseCallbacks.set(key, []);
    releaseCallbacks.get(key).push(cb);
  };

  const bloomreachOwns = (surface) => claimedSurfaces.has(String(surface));

  const createDedupeSet = () => {
    const seen = new Set();
    return {
      add: (key) => {
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      },
      has: (key) => seen.has(key),
      clear: () => seen.clear(),
    };
  };

  // Lee el payload Polar desde los data-* que emite el snippet alohas-polar-product-tags (modo attrs).
  const readItemData = (el) => {
    const id = Number(el.dataset.productId);
    return {
      product_id: Number.isFinite(id) ? id : el.dataset.productId,
      style_av: el.dataset.polarStyleAv ?? "none",
      priced: el.dataset.polarPriced ?? "none",
      delivery_g: el.dataset.polarDeliveryG ?? "none",
      product_cluster: el.dataset.polarProductCluster ?? "none",
      status: el.dataset.polarStatus ?? "none",
      sale: el.dataset.polarSale ?? "none",
    };
  };

  // Motor de carrusel (ST-4): 1 evento por producto visible ENTERO, con dedupe por carrusel.
  const observeCarouselItems = (
    rootEl,
    {
      location,
      itemSelector = "[data-product-id]",
      getProductData = readItemData,
      threshold = 0.9,
      extraData = {},
      seen,
    } = {}
  ) => {
    if (!rootEl) return { disconnect: () => {} };

    // seen externo -> persiste el dedupe entre reaperturas (cart drawer: sin dobles disparos)
    const fired = seen ?? new Set();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // isIntersecting con threshold alto = "visible entero"; el medio-visible no dispara
          if (!entry.isIntersecting) continue;

          const el = entry.target;
          const data = getProductData(el);
          const key = data?.product_id;
          observer.unobserve(el);

          if (key == null || fired.has(key)) continue;
          fired.add(key);
          track("carrousel_list_view", { ...data, location, ...extraData });
        }
      },
      { root: null, threshold }
    );

    rootEl.querySelectorAll(itemSelector).forEach((el) => observer.observe(el));

    return { disconnect: () => observer.disconnect() };
  };

  // getItemData expuesto para carruseles que no se basan en scroll (ej. focus carousel desktop).
  window.alohasPolar = {
    track,
    createDedupeSet,
    observeCarouselItems,
    getItemData: readItemData,
    claimSurface,
    releaseSurface,
    onSurfaceReleased,
    bloomreachOwns,
  };

  // Pre-calienta la base y vacia la cola cuando el visitante concede el consentimiento tras la carga.
  document.addEventListener("visitorConsentCollected", (event) => {
    if (event.detail?.analyticsAllowed) getBasePromise();
    flushPending();
  });
})();
